import React from 'react';
import { Coffee } from 'lucide-react';
import { getProductImageUrl } from '../utils/imageUtils';

const ProductCard = ({ 
  product, 
  theme, 
  customization, 
  onAddToCart, 
  isPreview = false,
  isFeatured = false
}) => {
  // Use theme layout properties first, then fallback to customization
  const layout = theme?.layout || {};
  const cardStyle = layout.productCardStyle || customization?.productCardStyle || 'vertical';
  const showImages = layout.showImages !== undefined ? layout.showImages : (customization?.showImages !== undefined ? customization.showImages : true);
  const showDescriptions = layout.showDescriptions !== undefined ? layout.showDescriptions : (customization?.showDescriptions !== undefined ? customization.showDescriptions : true);

  const handleProductClick = () => {
    if (!isPreview && onAddToCart) {
      onAddToCart(product);
    }
  };

  const handleAddClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (!isPreview && onAddToCart) {
      onAddToCart(product);
    }
  };

  if (cardStyle === 'horizontal') {
    return (
      <div
        key={product.id}
        className={`flex rounded-2xl overflow-hidden shadow-lg transition-all duration-200 hover:scale-105 ${!isPreview ? 'cursor-pointer' : ''}`}
        style={{ 
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`
        }}
        onClick={handleProductClick}
      >
        {showImages && (
          <div className="w-32 h-32 flex-shrink-0">
            {product.imageUrl ? (
              <img
                src={isPreview ? product.imageUrl : getProductImageUrl(product.imageUrl)}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : (
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: theme.colors.border }}
              >
                <Coffee 
                  className="w-12 h-12" 
                  style={{ color: theme.colors.textSecondary }}
                />
              </div>
            )}
            {product.imageUrl && (
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{ 
                  backgroundColor: theme.colors.border,
                  display: 'none'
                }}
              >
                <Coffee 
                  className="w-12 h-12" 
                  style={{ color: theme.colors.textSecondary }}
                />
              </div>
            )}
          </div>
        )}
        
        <div className="flex-1 p-4 flex flex-col justify-between">
          <div>
            <h3 
              className="text-xl font-bold mb-2 leading-tight"
              style={{ 
                color: theme.colors.text,
                fontFamily: theme.typography.headingFont
              }}
            >
              {product.name}
            </h3>
            {showDescriptions && product.description && (
              <p 
                className="text-sm mb-3 line-clamp-2"
                style={{ 
                  color: theme.colors.textSecondary,
                  fontFamily: theme.typography.bodyFont
                }}
              >
                {product.description}
              </p>
            )}
            <div className="flex items-center justify-between">
              <span 
                className="text-2xl font-bold"
                style={{ color: theme.colors.accent }}
              >
                ${product.price}
              </span>
              {!isPreview && (
                <button
                  onClick={handleAddClick}
                  className="px-6 py-3 rounded-xl font-bold text-lg transition-all duration-200 border-2 shadow-md coffee-button add-button"
                  style={{ 
                    backgroundColor: theme.colors.primary,
                    color: '#FFFFFF',
                    borderColor: theme.colors.primary,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme.colors.secondary;
                    e.target.style.borderColor = theme.colors.secondary;
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme.colors.primary;
                    e.target.style.borderColor = theme.colors.primary;
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  Add
                </button>
              )}
              {isPreview && (
                <div
                  className="px-6 py-3 rounded-xl font-bold text-lg border-2 shadow-md"
                  style={{ 
                    backgroundColor: theme.colors.primary,
                    color: '#FFFFFF',
                    borderColor: theme.colors.primary,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                  }}
                >
                  Add
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cardStyle === 'vertical') {
    return (
      <div
        key={product.id}
        className={`flex flex-col rounded-2xl overflow-hidden shadow-lg transition-all duration-200 hover:scale-105 ${!isPreview ? 'cursor-pointer' : ''}`}
        style={{ 
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`
        }}
        onClick={handleProductClick}
      >
        {showImages && (
          <div className="aspect-square">
            {product.imageUrl ? (
              <img
                src={isPreview ? product.imageUrl : getProductImageUrl(product.imageUrl)}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : (
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: theme.colors.border }}
              >
                <Coffee 
                  className="w-12 h-12" 
                  style={{ color: theme.colors.textSecondary }}
                />
              </div>
            )}
            {product.imageUrl && (
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{ 
                  backgroundColor: theme.colors.border,
                  display: 'none'
                }}
              >
                <Coffee 
                  className="w-12 h-12" 
                  style={{ color: theme.colors.textSecondary }}
                />
              </div>
            )}
          </div>
        )}
        
        <div className="p-4">
          <h3 
            className="text-xl font-bold mb-2"
            style={{ 
              color: theme.colors.text,
              fontFamily: theme.typography.headingFont
            }}
          >
            {product.name}
          </h3>
          {customization.showDescriptions && product.description && (
            <p 
              className="text-sm mb-3"
              style={{ 
                color: theme.colors.textSecondary,
                fontFamily: theme.typography.bodyFont
              }}
            >
              {product.description}
            </p>
          )}
          <div className="flex items-center justify-between">
            <span 
              className="text-2xl font-bold"
              style={{ color: theme.colors.accent }}
            >
              ${product.price}
            </span>
            {!isPreview && (
              <button
                onClick={handleAddClick}
                className="px-6 py-3 rounded-xl font-bold text-lg transition-all duration-200 border-2 shadow-md"
                style={{ 
                  backgroundColor: theme.colors.primary,
                  color: '#FFFFFF',
                  borderColor: theme.colors.primary,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.colors.secondary;
                  e.target.style.borderColor = theme.colors.secondary;
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme.colors.primary;
                  e.target.style.borderColor = theme.colors.primary;
                  e.target.style.transform = 'scale(1)';
                }}
              >
                Add
              </button>
            )}
            {isPreview && (
              <div
                className="px-6 py-3 rounded-xl font-bold text-lg border-2 shadow-md"
                style={{ 
                  backgroundColor: theme.colors.primary,
                  color: '#FFFFFF',
                  borderColor: theme.colors.primary,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                }}
              >
                Add
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Sheet style - two-column layout inspired by traditional menus
  if (cardStyle === 'sheet') {
    return (
      <div
        key={product.id}
        className={`flex justify-between items-start border-b pb-4 mb-4 transition-all duration-200 ${!isPreview ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        style={{ 
          borderColor: theme.colors.border,
          fontFamily: theme.typography.bodyFont
        }}
        onClick={handleProductClick}
      >
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h3 
                className="font-bold mb-1 leading-tight"
                style={{ 
                  color: theme.colors.text,
                  fontFamily: theme.typography.headingFont,
                  fontSize: `${theme.typography.headingSize - 4}px`
                }}
              >
                {product.name}
              </h3>
              {showDescriptions && product.description && (
                <p 
                  className="text-sm mb-2"
                  style={{ 
                    color: theme.colors.textSecondary,
                    fontFamily: theme.typography.bodyFont
                  }}
                >
                  {product.description}
                </p>
              )}
            </div>
            <div className="text-right ml-4">
              <span 
                className="text-xl font-bold"
                style={{ color: theme.colors.accent }}
              >
                ${typeof product.price === 'number' ? product.price.toFixed(2) : product.price}
              </span>
              {!isPreview && (
                <div className="mt-2">
                  <button
                    onClick={handleAddClick}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border-2 shadow-sm"
                    style={{ 
                      backgroundColor: theme.colors.primary,
                      color: '#FFFFFF',
                      borderColor: theme.colors.primary,
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = theme.colors.secondary;
                      e.target.style.borderColor = theme.colors.secondary;
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = theme.colors.primary;
                      e.target.style.borderColor = theme.colors.primary;
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Featured style - larger card for hero products
  if (cardStyle === 'featured' && isFeatured) {
    return (
      <div
        key={product.id}
        className={`relative rounded-2xl overflow-hidden shadow-xl transition-all duration-200 ${!isPreview ? 'cursor-pointer hover:scale-105' : ''} mb-8`}
        style={{ 
          backgroundColor: theme.colors.surface,
          border: `2px solid ${theme.colors.border}`
        }}
        onClick={handleProductClick}
      >
        {showImages && (
          <div className="h-64 relative">
            {product.imageUrl ? (
              <img
                src={isPreview ? product.imageUrl : getProductImageUrl(product.imageUrl)}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : (
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: theme.colors.border }}
              >
                <Coffee 
                  className="w-16 h-16" 
                  style={{ color: theme.colors.textSecondary }}
                />
              </div>
            )}
            {product.imageUrl && (
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{ 
                  backgroundColor: theme.colors.border,
                  display: 'none'
                }}
              >
                <Coffee 
                  className="w-16 h-16" 
                  style={{ color: theme.colors.textSecondary }}
                />
              </div>
            )}
            {/* Featured Badge */}
            <div 
              className="absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-bold"
              style={{ 
                backgroundColor: theme.colors.accent,
                color: theme.colors.text
              }}
            >
              Featured
            </div>
          </div>
        )}
        
        <div className="p-6">
          <h3 
            className="text-3xl font-bold mb-3 leading-tight"
            style={{ 
              color: theme.colors.text,
              fontFamily: theme.typography.headingFont
            }}
          >
            {product.name}
          </h3>
          {showDescriptions && product.description && (
            <p 
              className="text-lg mb-4"
              style={{ 
                color: theme.colors.textSecondary,
                fontFamily: theme.typography.bodyFont
              }}
            >
              {product.description}
            </p>
          )}
          <div className="flex items-center justify-between">
            <span 
              className="text-4xl font-bold"
              style={{ color: theme.colors.accent }}
            >
              ${typeof product.price === 'number' ? product.price.toFixed(2) : product.price}
            </span>
            {!isPreview && (
              <button
                onClick={handleAddClick}
                className="px-8 py-4 rounded-xl font-bold text-xl transition-all duration-200 border-2 shadow-lg"
                style={{ 
                  backgroundColor: theme.colors.primary,
                  color: '#FFFFFF',
                  borderColor: theme.colors.primary,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.colors.secondary;
                  e.target.style.borderColor = theme.colors.secondary;
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme.colors.primary;
                  e.target.style.borderColor = theme.colors.primary;
                  e.target.style.transform = 'scale(1)';
                }}
              >
                Add to Cart
              </button>
            )}
            {isPreview && (
              <div
                className="px-8 py-4 rounded-xl font-bold text-xl border-2 shadow-lg"
                style={{ 
                  backgroundColor: theme.colors.primary,
                  color: '#FFFFFF',
                  borderColor: theme.colors.primary,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                }}
              >
                Add to Cart
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Fallback for unknown card styles
  return null;
};

export default ProductCard;
